import type { KnownMapping } from "@/types";

/**
 * Builds schema_id → display-name options for Known Mappings schema pills.
 * When providers are selected, only schemas that appear on mappings attributed
 * to those providers are included — so pills cannot offer empty intersections.
 */
export function schemasForProviderFilter(
  mappings: KnownMapping[],
  selectedProviderIds: string[]
): Map<string, string> {
  const scoped =
    selectedProviderIds.length === 0
      ? mappings
      : mappings.filter((m) => !!m.providerId && selectedProviderIds.includes(m.providerId));

  const map = new Map<string, string>();
  for (const m of scoped) {
    if (!m.schemaId) continue;
    map.set(m.schemaId, m.schemaName || m.schemaId);
  }
  return map;
}

/**
 * Overlay catalogue/assignment names onto a schema_id → label map.
 * Catalogue wins when it has a real name; otherwise keep the mapping-derived
 * label unless it is just the raw schema_id (UUID fallback).
 */
export function mergeSchemaDisplayNames(
  schemaIdsWithFallbackNames: Map<string, string>,
  catalogueNameById: Map<string, string>
): Map<string, string> {
  const out = new Map<string, string>();
  for (const [schemaId, fallback] of schemaIdsWithFallbackNames) {
    const catalogue = catalogueNameById.get(schemaId)?.trim();
    if (catalogue) {
      out.set(schemaId, catalogue);
      continue;
    }
    const fromRow = fallback?.trim();
    if (fromRow && fromRow !== schemaId) {
      out.set(schemaId, fromRow);
      continue;
    }
    out.set(schemaId, schemaId);
  }
  return out;
}

/** Resolve a single schema display label from the merged map. */
export function resolveSchemaDisplayName(
  schemaId: string | undefined,
  schemaName: string | undefined,
  displayNameById: Map<string, string>
): string {
  if (!schemaId) return schemaName?.trim() || "—";
  const fromMap = displayNameById.get(schemaId);
  if (fromMap) return fromMap;
  if (schemaName && schemaName !== schemaId) return schemaName;
  return schemaId;
}
